import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { FlattenedInvoiceData } from '../../types/invoice';
import { trackEvent } from '../../utils/analytics';
import Header from '../layout/Header';
import Footer from '../layout/Footer';
import LoadingOverlay from './shared/LoadingOverlay';
import UploadSection from './shared/UploadSection';
import Stepper from './shared/Stepper';
import { scrollbarClass, isCacheExpired } from './shared/constants';
import Modal from '../common/Modal';
import SignIn from '../auth/SignIn';
import { supabase } from '../../utils/supabaseClient';
import { FiUpload } from 'react-icons/fi';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { XMLParser } from 'fast-xml-parser';
import { jsPDF } from "jspdf";
import 'jspdf-autotable';
// ConversionToggle removed - using direct navigation from header
import InvoiceViewer from './InvoiceViewer';
import { transformInvoiceData } from '../../config/invoiceFields';
import { useTranslation } from '../../hooks/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../shared/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLocaleFromLanguage } from '../../utils/formatters';
import SubscriptionModal from '../common/SubscriptionModal';
import SEOContent from '../common/SEOContent';

// Register XML language for syntax highlighting
SyntaxHighlighter.registerLanguage('xml', xml);

const steps: string[] = [
    'converter.steps.uploadXml',
    'converter.steps.viewData',
    'converter.steps.downloadPdf'
];

// Helper to format currency
const formatCurrency = (amount: string | number | undefined, currency: string = 'EUR', locale: string = 'de-DE'): string => {
    if (!amount) return '';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
    }).format(num);
};

// Add this function to generate PDF
const generatePdf = async (invoiceData: FlattenedInvoiceData, t: (key: string) => string, locale: string = 'de-DE'): Promise<Blob> => {
    const doc = new jsPDF();

    // Helper function to safely format text
    const formatText = (text: any): string => {
        if (!text) return '';
        return String(text).trim();
    };

    // Helper function to safely join address parts
    const formatAddress = (parts: (string | undefined)[]): string[] => {
        return parts.filter(part => part).map(formatText);
    };

    // Set font
    doc.setFont('helvetica');

    // Add header
    doc.setFontSize(14);
    doc.text('Invoice', 20, 20);

    // Add seller details
    doc.setFontSize(12);
    doc.text('From:', 20, 35);
    doc.setFontSize(10);

    const sellerAddress = formatAddress([
        invoiceData['Seller.LegalName'],
        invoiceData['Seller.Street'],
        `${invoiceData['Seller.PostalCode']} ${invoiceData['Seller.City']}`
    ]);

    if (sellerAddress.length > 0) {
        doc.text(sellerAddress, 20, 45);
    }

    // Add buyer details
    doc.setFontSize(12);
    doc.text('To:', 20, 70);
    doc.setFontSize(10);

    const buyerAddress = formatAddress([
        invoiceData['Buyer.LegalName'],
        invoiceData['Buyer.Street'],
        `${invoiceData['Buyer.PostalCode']} ${invoiceData['Buyer.City']}`
    ]);

    if (buyerAddress.length > 0) {
        doc.text(buyerAddress, 20, 80);
    }

    // Add invoice number and date
    doc.setFontSize(12);
    doc.text(`Invoice Number: ${formatText(invoiceData.ID)}`, 20, 105);
    doc.text(`Date: ${formatText(invoiceData.IssueDate)}`, 150, 105);

    // Add invoice items table
    doc.setFontSize(12);
    doc.text('Invoice Items', 20, 120);

    // Create table header with properly resolved translation keys
    const tableConfig = {
        head: [[
            t('invoice.table.description'),
            t('invoice.table.quantity'),
            t('invoice.table.unitPrice'),
            t('invoice.table.netAmount')
        ]],
        body: invoiceData.InvoiceLines.map(line => [
            line.Item?.Description || line.Item?.Name || '',
            `${line.InvoicedQuantity}`,
            formatCurrency(line.Price?.PriceAmount || 0, 'EUR', locale),
            formatCurrency(line.LineExtensionAmount || 0, 'EUR', locale)
        ])
    };

    // Add table
    (doc as any).autoTable({
        ...tableConfig,
        startY: 125,
        theme: 'grid',
        styles: {
            fontSize: 10,
            cellPadding: 5,
            textColor: [0, 0, 0] // Set text color to black
        },
        headStyles: {
            fillColor: [128, 128, 128],
            textColor: [255, 255, 255],
            fontStyle: 'bold'
        },
        bodyStyles: {
            textColor: [0, 0, 0] // Set body text color to black
        },
        columnStyles: {
            0: { cellWidth: 80 }, // Description
            1: { cellWidth: 30 }, // Quantity
            2: { cellWidth: 30 }, // VAT Rate
            3: { cellWidth: 40, halign: 'right' }  // Unit Price
        }
    });

    const finalY = (doc as any).lastAutoTable.finalY + 10;

    // Add totals
    doc.setFontSize(10);
    doc.text(`Subtotal: ${formatCurrency(invoiceData['Total.NetAmount'], 'EUR', locale)}`, 130, finalY + 10);
    doc.text(`Tax: ${formatCurrency(invoiceData['Tax.Amount'], 'EUR', locale)}`, 130, finalY + 20);
    doc.setFontSize(12);
    doc.text(`Total: ${formatCurrency(invoiceData['Total.PayableAmount'], 'EUR', locale)}`, 130, finalY + 30);

    // Add payment information
    doc.setFontSize(12);
    doc.text('Payment Details', 20, finalY + 50);
    doc.setFontSize(10);

    const paymentInfo = [
        `Account Holder: ${invoiceData['Seller.LegalName']}`,
        `IBAN: ${invoiceData['Payment.IBAN']}`,
        `Due Date: ${invoiceData.DueDate}`
    ];

    doc.text(paymentInfo, 20, finalY + 60);

    // Add contact information
    doc.setFontSize(12);
    doc.text('Contact Information', 20, finalY + 90);
    doc.setFontSize(10);

    const contactInfo = formatAddress([
        `Name: ${invoiceData['Seller.ContactName']}`,
        `Phone: ${invoiceData['Seller.Phone']}`,
        `Email: ${invoiceData['Seller.Email']}`
    ]);

    doc.text(contactInfo, 20, finalY + 100);

    return doc.output('blob');
};

function findInvoiceRoot(result: any) {
    if (result.Invoice) return result.Invoice;
    if (result['ubl:Invoice']) return result['ubl:Invoice'];
    if (result.CrossIndustryInvoice) return result.CrossIndustryInvoice;
    if (result['rsm:CrossIndustryInvoice']) return result['rsm:CrossIndustryInvoice'];
    return null;
}

const XMLToPDFConversion: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [invoiceData, setInvoiceData] = useState<FlattenedInvoiceData | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [showSignInModal, setShowSignInModal] = useState(false);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const { user, isSubscribed, loading: authLoading } = useAuth(); // Added authLoading from useAuth
    const [isNextLoading, setIsNextLoading] = useState(false); // Added isNextLoading state
    const [processingStartTime, setProcessingStartTime] = useState<number | undefined>(undefined);
    const { language } = useLanguage();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [showBanner, setShowBanner] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);

    // Restore cached state if available
    useEffect(() => {
        const cached = sessionStorage.getItem('xmlToPdfState');
        if (cached) {
            const parsedCache = JSON.parse(cached);

            if (isCacheExpired(parsedCache.timestamp)) {
                sessionStorage.removeItem('xmlToPdfState');
                resetState();
                return;
            }

            if (parsedCache.invoiceData) {
                setInvoiceData(parsedCache.invoiceData);
            }

            if (parsedCache.file) {
                // Reconstruct the file from cache
                fetch(parsedCache.file.data)
                    .then(res => res.blob())
                    .then(blob => {
                        const restoredFile = new File([blob], parsedCache.file.name, { type: parsedCache.file.type });
                        setFile(restoredFile);
                    })
                    .catch(error => {
                        console.error('Error reconstructing file:', error);
                        sessionStorage.removeItem('xmlToPdfState');
                        resetState();
                    });
            }

            setActiveStep(parsedCache.activeStep ?? 0);
        }
    }, []);

    // Cache state
    useEffect(() => {
        const cacheState = async () => {
            if (!file && !invoiceData) return;

            let fileData = null;
            if (file) {
                const reader = new FileReader();
                const base64 = await new Promise<string>((resolve) => {
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(file);
                });

                fileData = {
                    data: base64,
                    name: file.name,
                    type: file.type
                };
            }

            const stateToCache = {
                file: fileData,
                invoiceData,
                activeStep,
                timestamp: Date.now()
            };

            sessionStorage.setItem('xmlToPdfState', JSON.stringify(stateToCache));
        };
        cacheState();
    }, [file, invoiceData, activeStep]);

    useEffect(() => {
        document.title = `${t('nav.convert')} XML ${t('common.to')} PDF | Invoice-Converter.com`;
        return () => {
            document.title = `1-Click PDF Invoice ${t('common.to')} XRechnung Converter | Invoice-Converter.com`;
        };
    }, []);

    const incrementConversionCount = async () => {
        if (!user) return;
        try {
            const { error } = await supabase.rpc('increment_conversions_count', {
                user_id: user.id
            });
            if (error) console.error('Error incrementing conversions count:', error);
        } catch (error) {
            console.error('Error incrementing conversions count:', error);
        }
    };

    const incrementDownloadsCount = async () => {
        if (!user) return;
        try {
            const { error } = await supabase.rpc('increment_downloads_count', {
                user_id: user.id
            });
            if (error) console.error('Error incrementing downloads count:', error);
        } catch (error) {
            console.error('Error incrementing downloads count:', error);
        }
    };

    const parseXmlContent = (xmlContent: string): FlattenedInvoiceData => {
        const parser = new XMLParser({
            ignoreAttributes: false,
            attributeNamePrefix: '@_',
            textNodeName: '#text',
            removeNSPrefix: true,
            parseAttributeValue: true,
            isArray: (name) => name === 'InvoiceLine'
        });

        const result = parser.parse(xmlContent);
        const invoice = findInvoiceRoot(result);

        if (!invoice) {
            throw new Error('Could not find Invoice element in XML');
        }

        return transformInvoiceData(invoice);
    };

    const handleFileChange = (files: File[]) => {
        if (files.length > 0) {
            resetState();
            const selectedFile = files[0];
            trackEvent('Upload', 'xml_file_selected', selectedFile.name, selectedFile.size);
            setFile(selectedFile);
        }
    };

    const onDrop = (acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            trackEvent('Upload', 'xml_file_dropped', acceptedFiles[0].name, acceptedFiles[0].size);
        }
        handleFileChange(acceptedFiles);
    };

    const dropzoneOptions: DropzoneOptions = {
        onDrop,
        accept: { 'text/xml': ['.xml'] },
        multiple: false,
        maxSize: 10000000, // 10MB
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions);

    const uploadAndExtract = async () => {
        if (!file) {
            trackEvent('Upload', 'upload_error', 'No file selected');
            setErrorMessage('Please select an XML file.');
            return;
        }

        setLoading(true);
        setProcessingStartTime(Date.now());
        try {
            const content = await file.text();
            const parsedData = parseXmlContent(content);
            setInvoiceData(parsedData);

            trackEvent(
                'Invoice',
                'xml_upload_success',
                `${file.name} | ${user?.email || 'anonymous'}`,
                file.size
            );

            await incrementConversionCount();
            setActiveStep(1);
        } catch (error: any) {
            console.error('Error parsing XML:', error);
            trackEvent('Upload', 'upload_error', error?.message || 'Unknown error');
            setErrorMessage(error.message || 'Failed to parse the XML file.');
        } finally {
            setLoading(false);
            setProcessingStartTime(undefined);
        }
    };

    const convertToPdf = async () => {
        if (!invoiceData || !user) return;
        if (!isSubscribed) {
            setShowSubscriptionModal(true);
            return;
        }

        setLoading(true);
        setProcessingStartTime(Date.now());
        try {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
                setPdfUrl(null);
            }

            const locale = getLocaleFromLanguage(language);
            const pdfBlob = await generatePdf(invoiceData, t, locale);
            const url = window.URL.createObjectURL(pdfBlob);
            setPdfUrl(url);
            trackEvent('conversion', 'xml_to_pdf_success', 'XML to PDF conversion successful');
            await incrementDownloadsCount();
            setActiveStep(2);
        } catch (err: any) {
            setErrorMessage('PDF generation failed. Please try again.');
            trackEvent('conversion', 'xml_to_pdf_error', 'XML to PDF generation failed');
            console.error('PDF generation error:', err);
        } finally {
            setLoading(false);
            setProcessingStartTime(undefined);
        }
    };

    const handleDownloadPdf = async (): Promise<void> => {
        if (!pdfUrl || !invoiceData) return;

        try {
            const a = document.createElement('a');
            a.href = pdfUrl;
            a.download = `invoice-${invoiceData.ID}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error: unknown) {
            console.error('Error in handleDownloadPdf:', error);
            // You might want to show an error message to the user here
        }
    };

    // Cleanup PDF URL when component unmounts
    useEffect(() => {
        return () => {
            if (pdfUrl) {
                window.URL.revokeObjectURL(pdfUrl);
            }
        };
    }, [pdfUrl]);

    const handleNext = async () => {
        trackEvent('Navigation', 'next_step', `Step ${activeStep + 1}`);
        setIsNextLoading(true); // Set loading true
        try {
            if (activeStep === 0) {
                await uploadAndExtract();
            } else if (activeStep === 1) {
                // Check auth before proceeding
                if (!user) {
                    setShowSignInModal(true);
                    setIsNextLoading(false); // Reset loading
                    return;
                }
                if (!isSubscribed) {
                    navigate(`/${language}/profile`);
                    setIsNextLoading(false); // Reset loading
                    return;
                }
                await convertToPdf();
            }
        } catch (error) {
            // Error handling is mostly within uploadAndExtract and convertToPdf
            // This catch is a fallback for unexpected issues during the step transition logic itself.
            console.error("Error in handleNext:", error);
            if (!errorMessage) { // Avoid overwriting more specific error messages
                 setErrorMessage(t('converter.errors.processingFailed') || 'An unexpected error occurred.');
            }
        } finally {
            setIsNextLoading(false); // Reset loading in finally block
        }
    };

    const handleBack = () => {
        trackEvent('Navigation', 'previous_step', `Step ${activeStep}`);
        setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const resetState = () => {
        setFile(null);
        setInvoiceData(null);
        setActiveStep(0);
        setErrorMessage('');
        setPdfUrl(null);
        setLoading(false);
        setProcessingStartTime(undefined);
        sessionStorage.removeItem('xmlToPdfState');

        // Cleanup PDF URL if it exists
        if (pdfUrl) {
            window.URL.revokeObjectURL(pdfUrl);
        }
    };

    // Add this effect to cleanup loading state if component unmounts while loading
    useEffect(() => {
        return () => {
            if (loading) {
                setLoading(false);
                setProcessingStartTime(undefined);
            }
        };
    }, [loading]);

    return (
        <div className="min-h-screen bg-[var(--bg-primary)]">
            <Header />
            <div className="container mx-auto px-4 pt-20">
                {/* Conversion Toggle removed - using direct navigation from header */}

                {/* Headlines and Subheadlines */}
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[var(--text-primary)] mb-2">
                        <span className="text-[var(--brand-primary)]">XRechnung</span> {t('common.to')} <span className="text-[var(--brand-primary)]">PDF</span> {t('converter.converter')}
                    </h1>
                    {/* Conditionally render the subtitle */}
                    {activeStep !== 1 && activeStep !== 2 && (
                        <p className="text-xl text-[var(--text-tertiary)]">
                            {t('converter.xmlToPdf.subtitle')}
                        </p>
                    )}
                </div>

                {/* Separator Line */}
                <div className="w-full border-t border-[var(--border-primary)] mb-8"></div>

                {/* Steps Section */}
                <div className="mb-8">
                    <Stepper steps={steps} activeStep={activeStep} />
                </div>

                {/* Main Content Area */}
                <div className="mb-16">
                    {activeStep === 0 && (
                        <div className="relative">
                            <UploadSection
                                getRootProps={getRootProps}
                                getInputProps={getInputProps}
                                isDragActive={isDragActive}
                                file={file}
                                setFile={setFile}
                                fileType="xml"
                                loading={loading}
                                processingStartTime={processingStartTime}
                            />
                        </div>
                    )}
                    {activeStep === 1 && (
                        <div className="flex gap-4 h-[800px] overflow-hidden">
                            <div className={`w-full overflow-y-auto ${scrollbarClass}`}>
                                {invoiceData && (
                                    <InvoiceViewer
                                        invoiceData={invoiceData}
                                        readOnly={true}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                    {activeStep === 2 && (
                        <div className="flex flex-col items-center space-y-6 w-full max-w-4xl mx-auto">
                            <div className="w-full h-[60vh] rounded-xl p-4">
                                {pdfUrl && (
                                    <iframe
                                        src={pdfUrl}
                                        className="w-full h-full rounded-lg"
                                        title={t('converter.pdfPreview')}
                                    />
                                )}
                            </div>
                            <div className="flex space-x-4">
                                <Button
                                    onClick={handleDownloadPdf}
                                    disabled={!pdfUrl}
                                >
                                    {t('converter.buttons.downloadPdf')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between px-20 mb-8">
                    <Button
                        variant="secondary"
                        onClick={handleBack}
                        disabled={activeStep === 0}
                    >
                        {t('converter.buttons.back')}
                    </Button>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            onClick={resetState}
                        >
                            {t('converter.buttons.convertAnother')}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleNext}
                            disabled={(activeStep === 0 && !file) || loading || isNextLoading || authLoading}
                        >
                            {t('converter.buttons.next')}
                        </Button>
                    )}
                </div>

                {/* Error Message */}
                {errorMessage && (
                    <div className="mt-6 p-4 bg-[var(--error)]/10 border border-[var(--error)]/20 text-[var(--error)] rounded-lg text-center">
                        {errorMessage}
                    </div>
                )}
            </div>
            
            {/* SEO Content Section - Only show on initial upload step */}
            {activeStep === 0 && (
                <div className="container mx-auto px-4">
                    <SEOContent
                        title={t('seoContent.xrechnungToPdf.title')}
                        description={t('seoContent.xrechnungToPdf.description')}
                        features={t('seoContent.xrechnungToPdf.features')}
                        benefits={t('seoContent.xrechnungToPdf.benefits')}
                        howItWorks={t('seoContent.xrechnungToPdf.howItWorks')}
                        faq={t('seoContent.xrechnungToPdf.faq')}
                        currentTool="xrechnungToPdf"
                    />
                </div>
            )}
            
            <Footer />
            {/* Sign In Modal */}
            <Modal isOpen={showSignInModal} onClose={() => setShowSignInModal(false)}>
                <SignIn isModal onClose={() => setShowSignInModal(false)} />
            </Modal>
            
            {/* Subscription Banner */}
            {showBanner && (
                <div className="fixed bottom-0 left-0 right-0 bg-[var(--brand-primary)]/90 text-[var(--text-button)] p-4 animate-fade-in-up">
                    <div className="max-w-4xl mx-auto flex justify-between items-center">
                        <p className="font-medium">
                            {t('pricing.subscriptionNeeded')}
                        </p>
                        <button
                            onClick={() => setShowBanner(false)}
                            className="ml-4 p-2 hover:bg-[var(--bg-primary)]/10 rounded-full transition-colors"
                            aria-label="Close banner"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}
            <SubscriptionModal 
                isOpen={showSubscriptionModal} 
                onClose={() => setShowSubscriptionModal(false)} 
            />
        </div>
    );
};

export default XMLToPDFConversion;